import {Import, ListChecks, Plus} from 'lucide-react';
import * as React from 'react';
import {useHotkeys} from 'react-hotkeys-hook';
import {Button} from 'src/Button';
import {CategoryFilterInput} from 'src/CategoryFilterInput';
import ActionHeader from 'src/components/ActionHeader';
import {AmountFilterSelect} from 'src/components/AmountFilterSelect';
import {DateFilterSelect} from 'src/components/DateFilterSelect';
import {FilterDropdown} from 'src/components/FilterDropdown';
import {FilterPillRow} from 'src/components/FilterPillRow';
import {DimensionsFilterInput} from 'src/DimensionsFilterInput';
import {useModals} from 'src/Modals';
import {PopoverMenu} from 'src/PopoverMenu';
import PopoverMenuItem from 'src/PopoverMenuItem';
import {SearchInput} from 'src/SearchInput';
import {
  PAYMENT_KINDS,
  TRANSACTION_KIND_LABELS,
  TRANSFER_KINDS,
  isTransactionKind,
} from 'src/transactionKinds';
import TransactionFilters from 'src/TransactionListView/TransactionFilters';

type Props = {
  filters: TransactionFilters;
  isSelectMode: boolean;
  onUpdateFilters: (newFilters: TransactionFilters) => void;
  onEnterSelectMode: () => void;
  onClearSelection: () => void;
};

// The tiered Type menu: Payments (Spending, Income) then Transfers (the
// subtypes plus "No subtype"), each group header toggling its whole tier.
const kindOptions = [
  ...PAYMENT_KINDS.map((kind) => ({
    key: kind,
    label: TRANSACTION_KIND_LABELS[kind],
    group: 'Payments',
  })),
  ...TRANSFER_KINDS.map((kind) => ({
    key: kind,
    label: TRANSACTION_KIND_LABELS[kind],
    group: 'Transfers',
  })),
];

export const Header = (props: Props) => {
  const {
    filters,
    isSelectMode,
    onUpdateFilters,
    onEnterSelectMode,
    onClearSelection,
  } = props;

  const modals = useModals();

  const anyModalOpen =
    modals.createRawTransactionModal.state.open ||
    modals.importTransactionsModal.state.open;

  useHotkeys(
    'a',
    (e) => {
      if (anyModalOpen) return;
      if (isSelectMode) return;
      e.preventDefault();
      modals.createRawTransactionModal.openModal({});
    },
    [isSelectMode, anyModalOpen],
  );

  const openImport = React.useCallback(() => {
    modals.importTransactionsModal.openModal({});
  }, [modals.importTransactionsModal]);

  useHotkeys(
    'Escape',
    () => {
      if (isSelectMode) {
        onClearSelection();
      }
    },
    [isSelectMode, onClearSelection],
  );

  const hasActiveFilters = !filters.equals(TransactionFilters.empty);
    const headerContent = (
      <FilterPillRow
        pills={[
          {
            key: 'dates',
            active: filters.startDate != null || filters.endDate != null,
            pill: (
              <DateFilterSelect
                value={{
                  startDate: filters.startDate,
                  endDate: filters.endDate,
                }}
                onChange={({startDate, endDate}) =>
                  onUpdateFilters(filters.merge({startDate, endDate}))
                }
              />
            ),
          },
          {
            key: 'type',
            active: filters.kinds != null,
            pill: (
              <FilterDropdown
                multiple
                label="Type"
                allLabel="All Transactions"
                options={kindOptions}
                value={filters.kinds ?? []}
                onChange={(keys) =>
                  onUpdateFilters(
                    filters.merge({kinds: keys.filter(isTransactionKind)}),
                  )
                }
              />
            ),
          },
          {
            key: 'amount',
            active: filters.minAmount != null || filters.maxAmount != null,
            pill: (
              <AmountFilterSelect
                value={{
                  minAmount: filters.minAmount,
                  maxAmount: filters.maxAmount,
                }}
                onChange={({minAmount, maxAmount}) =>
                  onUpdateFilters(filters.merge({minAmount, maxAmount}))
                }
              />
            ),
          },
          {
            key: 'categories',
            active: filters.categories != null,
            pill: (
              <CategoryFilterInput
                categories={filters.categories}
                setCategories={(categories) =>
                  onUpdateFilters(filters.merge({categories}))
                }
              />
            ),
          },
          {
            key: 'dimensions',
            active: filters.tags != null || filters.fieldFilters != null,
            pill: (
              <DimensionsFilterInput
                tags={filters.tags}
                setTags={(tags) => onUpdateFilters(filters.merge({tags}))}
                fieldFilters={filters.fieldFilters}
                setFieldFilters={(fieldFilters) =>
                  onUpdateFilters(filters.merge({fieldFilters}))
                }
              />
            ),
          },
        ]}
        onReset={
          hasActiveFilters
            ? () => onUpdateFilters(TransactionFilters.empty)
            : undefined
        }
      />
    );

  const overflowMenu = (
    <PopoverMenu>
      <PopoverMenuItem
        onSelect={() => modals.createRawTransactionModal.openModal({})}
        iconLeft={<Plus />}
      >
        Add transaction
      </PopoverMenuItem>
      <PopoverMenuItem onSelect={openImport} iconLeft={<Import />}>
        Import
      </PopoverMenuItem>
    </PopoverMenu>
  );

  // The search box rides the action row rather than the filter row (same as
  // the Activity page): it needs a width the pills don't, and pairing it with
  // Select and the overflow menu keeps the whole header to two rows on a
  // phone. The selection itself lives in the selection bar, so the filters
  // stay put while selecting.
  const actions = (
    <>
      <div className="min-w-0 flex-1 md:w-56 md:flex-none">
        <SearchInput
          initialQuery={filters.search}
          onSearch={(search) => onUpdateFilters(filters.merge({search}))}
          inputProps={{placeholder: 'Search'}}
        />
      </div>
      <Button
        variant="outline"
        className="shrink-0 gap-1.5 text-sm max-md:px-2.5"
        onClick={isSelectMode ? onClearSelection : onEnterSelectMode}
        aria-pressed={isSelectMode}
        aria-label={isSelectMode ? 'Done selecting' : 'Select transactions'}
      >
        <ListChecks className="h-4 w-4" />
        <span className="max-md:hidden">{isSelectMode ? 'Done' : 'Select'}</span>
      </Button>
      {overflowMenu}
    </>
  );

  return (
    <ActionHeader
      content={headerContent}
      actions={actions}
      stackActionsOnMobile
    />
  );
};
